import { useState, useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import { aiCreditService } from '../services/aiCreditService';
import { reportError } from '../lib/errorHandling';
import type { TenantSubscription } from '../lib/credits/types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const GRACE_DAYS = 7;

export type TrialState = 'paid' | 'trial' | 'grace' | 'locked' | 'none';

export interface TrialStatusResult {
  state: TrialState;
  /** For 'trial': days until period_end (ceil). For 'grace': grace days remaining (ceil). */
  daysLeft: number;
  plan: string | null;
}

export function computeTrialState(
  sub: TenantSubscription | null,
  now: Date,
): TrialStatusResult {
  if (!sub) return { state: 'none', daysLeft: 0, plan: null };

  const msPast = now.getTime() - new Date(sub.periodEnd).getTime();

  if (msPast < 0) {
    // Still within the subscription period.
    if (sub.plan !== 'trial') {
      return { state: 'paid', daysLeft: 0, plan: sub.plan };
    }
    return {
      state: 'trial',
      daysLeft: Math.ceil(-msPast / MS_PER_DAY),
      plan: sub.plan,
    };
  }

  // At or past period_end.
  if (msPast <= GRACE_DAYS * MS_PER_DAY) {
    return {
      state: 'grace',
      daysLeft: Math.ceil((GRACE_DAYS * MS_PER_DAY - msPast) / MS_PER_DAY),
      plan: sub.plan,
    };
  }

  return { state: 'locked', daysLeft: 0, plan: sub.plan };
}

// In-flight dedup only — the entry is cleared once it settles, so a later
// mount or a tab refocus refetches fresh (e.g. after an admin marks the tenant
// paid / extends the trial in the Hub). A permanent cache would keep a locked
// tenant locked until a full page reload.
const inflight = new Map<string, Promise<TenantSubscription | null>>();

function loadSubscription(tenantId: string): Promise<TenantSubscription | null> {
  let req = inflight.get(tenantId);
  if (!req) {
    req = aiCreditService
      .getLatestSubscription(tenantId)
      .finally(() => inflight.delete(tenantId));
    inflight.set(tenantId, req);
  }
  return req;
}

export interface SubscriptionStatus extends TrialStatusResult {
  loading: boolean;
}

export function useSubscriptionStatus(): SubscriptionStatus {
  const { tenantId } = useTenant();
  // undefined = not yet fetched; null = no subscription row
  const [sub, setSub] = useState<TenantSubscription | null | undefined>(undefined);

  useEffect(() => {
    if (!tenantId) {
      setSub(null);
      return;
    }
    let cancelled = false;

    const load = () =>
      loadSubscription(tenantId)
        .then((result) => {
          if (!cancelled) setSub(result);
        })
        .catch((err) => {
          if (!cancelled) {
            reportError('useSubscriptionStatus', err);
            setSub(null); // fail open — never lock due to a fetch error
          }
        });

    load();

    // Re-check on tab refocus so a Hub-side "mark paid" / "extend trial"
    // unlocks the tenant without requiring a manual full reload. We don't reset
    // `sub` to undefined here, so the gate never flashes on a benign refocus.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [tenantId]);

  const loading = sub === undefined;
  const result = computeTrialState(sub ?? null, new Date());

  return { ...result, loading };
}
